// @flow
import loading from "assets/loading.svg";
import { PDFFillerContext } from "pages/Contexts/PdfFillerContext";
import * as React from "react";
import { useDrop } from "react-dnd";
import styled from "styled-components";
import type { PdfPage as PdfPageType } from "types/pdf-page";

import FormInput from "./FormInput";
import PdfPageOverlay from "./PdfPageOverlay";

const StyledPage = styled.img`
  display: block;
  max-width: none;
`;

const PageContainer = styled.div`
  position: relative;
  background-color: white;
  display: inline-block;

  ${(props: { $noMarginTop: boolean }) => {
    if (!props.$noMarginTop) {
      return `
        &:first-child {
          margin-top: 10px;
        }
      `;
    }
    return "";
  }}

  ${(props: { $width: number, $height: number }) => {
    return `
      width: ${props.$width}px;
      height: ${props.$height}px;
    `;
  }}
`;

const Spinner = styled.img.attrs({
  src: loading,
})`
  position: absolute;
  top: 10%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

type Props = {
  page: PdfPageType,
};

function PdfPage({ page }: Props): React.Node {
  const [imageLoading, setImageLoading] = React.useState(true);

  const {
    setSelectedInputFieldId,
    inputFields,
    setInputFields,
    pages,
    pageRefs,
    zoom,
    setSettingsOpen,
    viewMode,
    resourceId,
  } = React.useContext(PDFFillerContext);

  const pageRef = pageRefs[page.id];

  const toggleInputObjectIsResizing = React.useCallback(
    (id, isResizing) => {
      setInputFields((prevInputFields) => ({
        ...prevInputFields,
        [id]: {
          ...prevInputFields[id],
          isResizing: Boolean(isResizing),
        },
      }));
    },
    [setInputFields],
  );

  const renderInputFields = React.useCallback(
    (key) => {
      const inputField = inputFields[key];

      if (inputField.file_page !== page.id) return null;

      return (
        <FormInput
          inputField={inputField}
          key={inputField.id}
          resourceId={resourceId}
          toggleInputObjectIsResizing={toggleInputObjectIsResizing}
        />
      );
    },
    [inputFields, page.id, resourceId, toggleInputObjectIsResizing],
  );

  const moveInputField = React.useCallback(
    (id, left, top, pageId) => {
      setInputFields((prevInputFields) => ({
        ...prevInputFields,
        [id]: {
          ...prevInputFields[id],
          left,
          top,
          file_page: pageId,
        },
      }));
    },
    [setInputFields],
  );

  const dragInputField = React.useCallback(
    (item, monitor) => {
      const delta = monitor.getDifferenceFromInitialOffset();

      if (item.file_page === page.id) {
        if (item.top + delta.y + item.height > page.height) {
          return undefined;
        }

        if (item.top + delta.y < 0) {
          return undefined;
        }
      }

      let top = item.top + delta.y / zoom;
      const left = item.left + delta.x / zoom;

      if (item.file_page !== page.id) {
        const oldPage = pages?.find((page) => page.id === item.file_page);
        if (!oldPage) return undefined;

        if (top > oldPage.height) {
          // going to page down
          top = top - page.height;
        } else {
          // going to page up
          top = top + page.height;
        }
      }

      moveInputField(item.id, left, top, page.id);

      return undefined;
    },
    [moveInputField, page, pages, zoom],
  );

  const [, drop] = useDrop(
    () => ({
      accept: ["FORM_INPUT"],
      drop: (item, monitor) => {
        return dragInputField(item, monitor);
      },
    }),
    [dragInputField],
  );

  const handleImageLoad = React.useCallback(() => {
    setImageLoading(false);
  }, []);

  const pageWidth = (page.width || 0) * zoom;
  const pageHeight = (page.height || 0) * zoom;

  return (
    <div ref={pageRef} style={{ textAlign: "center" }}>
      <PageContainer
        $height={pageHeight}
        $noMarginTop={viewMode === "response"}
        $width={pageWidth}
        ref={drop}
      >
        <PdfPageOverlay page={page} />
        {Object.keys(inputFields).map(renderInputFields)}
        <StyledPage
          alt={page.file_name}
          className="border border-gray-400"
          draggable="false"
          height={pageHeight}
          onClick={() => {
            setSelectedInputFieldId(null);
            setSettingsOpen(false);
          }}
          onLoad={handleImageLoad}
          src={page.file}
          width={pageWidth}
        />
        {imageLoading && <Spinner width={300 * zoom} />}
      </PageContainer>
    </div>
  );
}

export default PdfPage;
